import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gavel, Plus, History, Pencil, Trash2, Upload, Download, FileText, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { api } from '../lib/api';
import { savePackage } from '../lib/packageSave.mjs';
import { packageIdMissing } from '../lib/packageSave.mjs';
import { countWord } from '../lib/countWord.mjs';
import Modal from '../components/ui/Modal';
import {
  FieldGrid, toPayload, toFormState, usd, fmtDate, Chip, inputCls, downloadFile, uploadFile,
} from '../components/bids/FieldGrid';

// Bids & Specs: bid records (hang off CRM Watchlist projects), spec / architect tracking,
// the document library and the submittal package builder. All three partners edit.

const SPEC_LABEL = { named: 'Concria named', named_or_equal: 'Named or equal', not_named: 'Not named', competitor_named: 'Competitor named', in_review: 'In review' };
const SPEC_TONE = { named: 'green', named_or_equal: 'green', not_named: 'amber', competitor_named: 'red', in_review: 'blue' };
const OUTCOME_LABEL = { pending: 'Pending', submitted: 'Submitted', won: 'Won', lost: 'Lost', no_bid: 'No bid', cancelled: 'Cancelled' };
const OUTCOME_TONE = { pending: 'blue', submitted: 'indigo', won: 'green', lost: 'red', no_bid: 'gray', cancelled: 'gray' };
const ROLE_LABEL = { gc: 'GC', concrete_sub: 'Concrete sub', installer: 'Installer', polisher: 'Polisher', owner: 'Owner', architect: 'Architect', other: 'Other' };
const PARTY_STATUS = { bidding: 'Bidding', invited: 'Invited', quoted: 'Quoted', declined: 'Declined', awarded: 'Awarded', not_awarded: 'Not awarded' };
const KIND_LABEL = { ae_firm: 'A/E firm', owner_program: 'Owner program', gc_design: 'GC design team', manufacturer: 'Manufacturer', other: 'Other' };
const SPEC_REC_LABEL = { target: 'Target', sample_dropped: 'Sample dropped', in_review: 'In review', approved: 'Approved', specified: 'Specified', not_specified: 'Not specified' };
const SPEC_REC_TONE = { target: 'gray', sample_dropped: 'blue', in_review: 'indigo', approved: 'green', specified: 'green', not_specified: 'red' };
const DOC_LABEL = {
  pds: 'Product data sheet', tds: 'Technical data sheet', sds: 'Safety data sheet', coa: 'Certificate of analysis', sieve_chart: 'Sieve chart',
  spec_section: 'Spec section', install_guide: 'Installation guide', brochure: 'Brochure', case_study: 'Case study', photo: 'Photo', color_chart: 'Colour chart', other: 'Other',
};
const ACTION_LABEL = { create: 'Created', import: 'Imported', update: 'Edited', delete: 'Deleted', restore: 'Restored', file: 'File uploaded', build: 'PDF built', export: 'Exported' };
const opts = (m) => Object.entries(m).map(([value, label]) => ({ value, label }));
const isOpen = (b) => !b.outcome || b.outcome === 'pending' || b.outcome === 'submitted';
const daysTo = (d) => (d ? Math.round((new Date(`${d}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null);

const TABS = [['bids', 'Bids'], ['specs', 'Spec tracking'], ['docs', 'Document library'], ['packages', 'Submittal packages']];

export default function BidsPage() {
  const [tab, setTab] = useState('bids');
  const [options, setOptions] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/bids/options').then(setOptions).catch((e) => setError(e.message)); }, []);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Gavel size={20} /> Bids &amp; Specs</h1>
        <p className="text-sm text-[var(--text-secondary)]">Bids hang off Watchlist projects. Track who is bidding, whether Concria is in the spec, the material quote, and build submittal packages.</p>
      </div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-[var(--border)]">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === k ? 'border-primary font-semibold text-[var(--text-primary)]' : 'border-transparent text-[var(--text-secondary)]'}`}>{l}</button>
        ))}
      </div>
      {error && <p className="mb-3 text-sm text-rose-600">{error}</p>}
      {!options && !error && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      {options && tab === 'bids' && <BidsTab options={options} />}
      {options && tab === 'specs' && <SpecsTab options={options} />}
      {options && tab === 'docs' && <DocsTab options={options} />}
      {options && tab === 'packages' && <PackagesTab options={options} />}
    </div>
  );
}

// ── shared bits ───────────────────────────────────────────────────────────────
function HistoryModal({ kind, row, title, onClose }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const path = kind === 'notes' ? `/concria-meet/notes/${row.id}/history` : `/bids/${kind}/${row.id}/history`;
    api.get(path).then((r) => setRows(r.history)).catch((e) => setErr(e.message));
  }, [kind, row.id]);
  const show = (o) => (o ? Object.entries(o).filter(([k]) => !['id', 'created_by', 'deleted_by'].includes(k)).map(([k, v]) => `${k}: ${v == null ? 'blank' : typeof v === 'object' ? JSON.stringify(v) : v}`).join('; ') : '');
  return (
    <Modal title={`History: ${title}`} onClose={onClose}>
      {err && <p className="text-sm text-rose-600">{err}</p>}
      {!rows && !err && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}
      <ul className="space-y-2 text-sm">
        {(rows || []).map((h) => (
          <li key={h.id} className="rounded-lg border border-[var(--border)] p-2">
            <div className="text-xs text-[var(--text-secondary)]">{new Date(h.created_at).toLocaleString()} · {h.actor || 'system'} · {ACTION_LABEL[h.action] || h.action}</div>
            {h.action !== 'create' && h.action !== 'import' && h.after && <div className="mt-1 break-words text-xs">{show(h.after)}</div>}
          </li>
        ))}
        {rows && !rows.length && <li className="text-[var(--text-secondary)]">No history yet.</li>}
      </ul>
    </Modal>
  );
}

function FormModal({ title, fields, record, onSave, onClose }) {
  const [value, setValue] = useState(() => toFormState(fields, record || {}));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    const { payload, error } = toPayload(fields, value, record?.id ? record : null);
    if (error) { setErr(error); return; }
    if (record?.id && !Object.keys(payload).length) { onClose(); return; }
    setBusy(true);
    try { await onSave(payload); } catch (e) { setErr(e.message); setBusy(false); }
  }
  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-3xl">
      <FieldGrid fields={fields} value={value} onChange={setValue} />
      {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">Cancel</button>
        <button disabled={busy} onClick={save} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </Modal>
  );
}

const iconBtn = 'inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-slate-800';

function useList(path, key) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const seq = useRef(0); // only the latest load may update the list (Deleted or Show deleted switched since)
  // The view on screen now: an action's reload loads this view, never the one it started under.
  const shownPath = useRef(path);
  shownPath.current = path;
  const load = useCallback(async () => {
    const mine = ++seq.current;
    const view = shownPath.current;
    // Lands only while its view is still on screen: a switch drops it even before the new view's own load starts.
    const lands = () => mine === seq.current && view === shownPath.current;
    try { const r = await api.get(view); if (lands()) { setRows(r[key]); setErr(''); } } catch (e) { if (lands()) setErr(e.message); }
  }, [path, key]);
  useEffect(() => { load(); }, [load]);
  return { rows, err, load, setErr };
}

// ── Bids ──────────────────────────────────────────────────────────────────────
function bidFields(options) {
  const projects = options.projects.map((p) => ({ value: p.id, label: `${p.project_name ?? ''}${p.region ? ` (${p.region})` : ''}` }));
  const accounts = options.accounts.map((a) => ({ value: a.id, label: a.name }));
  const proposals = options.proposals.map((p) => ({ value: p.id, label: `${p.number || 'Draft'}: ${p.title || ''}` }));
  return [
    { key: 'project_id', label: 'Watchlist project', type: 'picker', options: projects, required: true, span: 2, hint: 'Bids hang off a CRM project. Add the project on the Watchlist first if it is not listed.' },
    { key: 'title', label: 'Bid title', required: true, span: 2 },
    { key: 'owner_name', label: 'Owner' },
    { key: 'owner_account_id', label: 'Owner (CRM company)', type: 'picker', options: accounts },
    { key: 'location', label: 'Location' },
    { key: 'sqft', label: 'Square feet', type: 'number' },
    { key: 'scope', label: 'Scope / products', type: 'textarea', rows: 2 },
    { key: 'bid_due_date', label: 'Bid due date', type: 'date' },
    { key: 'bid_due_note', label: 'Due date note' },
    { key: 'spec_status', label: 'Is Concria in the spec?', type: 'select', options: opts(SPEC_LABEL), empty: 'Unknown' },
    { key: 'spec_section', label: 'Spec section', hint: 'e.g. 03 35 16' },
    { key: 'spec_note', label: 'Spec note', type: 'textarea', rows: 2 },
    { key: 'installer_account_id', label: 'Installer partner (CRM company)', type: 'picker', options: accounts },
    { key: 'installer_name', label: 'Installer partner (name)' },
    { key: 'material_quote_sent', label: 'Material quote sent?', type: 'tri' },
    { key: 'quote_ref', label: 'Quote number' },
    { key: 'quote_date', label: 'Quote date', type: 'date' },
    { key: 'quote_amount_cents', label: 'Quote amount ($)', type: 'money' },
    { key: 'quote_to', label: 'Quote sent to' },
    { key: 'proposal_id', label: 'Linked portal proposal', type: 'select', options: proposals, empty: 'None' },
    { key: 'concria_offer_no', label: 'Concria offer number' },
    { key: 'ccs_lead', label: 'CCS lead', type: 'select', options: opts({ Chad: 'Chad', Steve: 'Steve', Chase: 'Chase' }) },
    { key: 'outcome', label: 'Outcome', type: 'select', options: opts(OUTCOME_LABEL), empty: 'Not recorded' },
    { key: 'outcome_date', label: 'Outcome date', type: 'date' },
    { key: 'outcome_note', label: 'Outcome note', type: 'textarea', rows: 2 },
    { key: 'federal', label: 'Federal', type: 'bool', checkLabel: 'Federal bid' },
    { key: 'long_lead', label: 'Long lead', type: 'bool', checkLabel: 'Long-lead bid' },
    { key: 'install_window', label: 'Install window', hint: 'e.g. Fall 2028' },
    { key: 'price_validity', label: 'Price validity' },
    { key: 'escalation_assumption', label: 'Price escalation assumption', type: 'textarea', rows: 2 },
    { key: 'tariff_assumption', label: 'Tariff assumption', type: 'textarea', rows: 2 },
    { key: 'freight_assumption', label: 'Freight assumption' },
    { key: 'freight_valid_until', label: 'Freight quote valid until', type: 'date' },
    { key: 'notes', label: 'Notes', type: 'textarea', rows: 4 },
  ];
}
const partyFields = (options) => [
  { key: 'name', label: 'Name', required: true },
  { key: 'account_id', label: 'CRM company', type: 'picker', options: options.accounts.map((a) => ({ value: a.id, label: a.name })) },
  { key: 'role', label: 'Role', type: 'select', options: opts(ROLE_LABEL), required: true },
  { key: 'status', label: 'Status', type: 'select', options: opts(PARTY_STATUS), empty: 'Unknown' },
  { key: 'contact_name', label: 'Contact' },
  { key: 'contact_email', label: 'Contact email' },
  { key: 'note', label: 'Note', type: 'textarea', rows: 2 },
];

function BidsTab({ options }) {
  const [filter, setFilter] = useState('open');
  const { rows, err, load, setErr } = useList(filter === 'deleted' ? '/bids?deleted=1' : '/bids', 'bids');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null);
  const fields = useMemo(() => bidFields(options), [options]);
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (rows || []).filter((b) => (filter === 'deleted' ? !!b.deleted_at : !b.deleted_at && (filter === 'all' || (filter === 'open' ? isOpen(b) : !isOpen(b))))
      && (!n || [b.title, b.owner_name, b.location, b.project?.project_name, ...(b.parties || []).flatMap((p) => [p.name, p.account_name])].some((x) => String(x || '').toLowerCase().includes(n))));
  }, [rows, filter, q]);

  async function saveBid(payload) {
    if (editing.id) await api.patch(`/bids/${editing.id}`, payload);
    else await api.post('/bids', payload);
    setEditing(null); load();
  }

  if (err && !rows) return <p className="text-sm text-rose-600">{err}</p>;
  if (!rows) return <p className="text-sm text-[var(--text-secondary)]">Loading bids…</p>;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select className={`${inputCls} w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter">
          <option value="open">Open bids</option><option value="decided">Decided</option><option value="all">All</option><option value="deleted">Deleted</option>
        </select>
        <input className={`${inputCls} min-w-0 flex-1 sm:max-w-xs`} placeholder="Search bid, owner, GC" value={q} onChange={(e) => setQ(e.target.value)} />
        <button onClick={() => setEditing({})} className="ml-auto flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"><Plus size={15} /> New bid</button>
      </div>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      <div className="space-y-2">
        {shown.map((b) => {
          const d = daysTo(b.bid_due_date);
          const gcs = (b.parties || []).filter((p) => p.role === 'gc' || p.role === 'concrete_sub');
          return (
            <button key={b.id} onClick={() => setOpen(b.id)} className="block w-full rounded-xl border border-[var(--border)] bg-white p-3 text-left hover:border-primary dark:bg-slate-900">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[var(--text-primary)]">{b.title}</div>
                  <div className="text-xs text-[var(--text-secondary)]">{[b.project?.project_name, b.owner_name, b.location].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {b.federal && <Chip tone="indigo">Federal</Chip>}
                  {b.long_lead && <Chip tone="indigo">Long lead</Chip>}
                  <Chip tone={SPEC_TONE[b.spec_status] || 'gray'}>{SPEC_LABEL[b.spec_status] || 'Spec unknown'}</Chip>
                  <Chip tone={OUTCOME_TONE[b.outcome] || 'gray'}>{OUTCOME_LABEL[b.outcome] || 'Outcome not recorded'}</Chip>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-[var(--text-secondary)] sm:grid-cols-3">
                <div>Due: {b.bid_due_date ? <span className={d != null && d >= 0 && d <= 14 && isOpen(b) ? 'font-semibold text-amber-700' : ''}>{fmtDate(b.bid_due_date)}{isOpen(b) && d != null ? (d < 0 ? ` (${-d} d ago)` : ` (in ${d} d)`) : ''}</span> : 'not set'}</div>
                <div className="truncate">Bidding: {gcs.length ? gcs.map((p) => p.account_name || p.name).join(', ') : 'none recorded'}</div>
                <div>Quote: {b.quote_ref || b.proposal?.number || (b.material_quote_sent ? 'sent' : b.material_quote_sent === false ? 'not sent' : 'unknown')}{b.quote_amount_cents != null ? ` · ${usd(b.quote_amount_cents)}` : ''}{b.quote_date ? ` · ${fmtDate(b.quote_date)}` : ''}</div>
              </div>
            </button>
          );
        })}
        {!shown.length && <p className="py-6 text-center text-sm text-[var(--text-secondary)]">No bids match.</p>}
      </div>
      {editing && <FormModal title={editing.id ? 'Edit bid' : 'New bid'} fields={fields} record={editing.id ? editing : null} onSave={saveBid} onClose={() => setEditing(null)} />}
      {open && <BidDetail id={open} options={options} onClose={() => { setOpen(null); load(); }} onEdit={(b) => { setOpen(null); setEditing(b); }} onError={setErr} />}
    </div>
  );
}

function Row({ label, children }) {
  if (children == null || children === '' || children === false) return null;
  return (<div className="grid grid-cols-3 gap-2 py-1 text-sm"><dt className="text-[var(--text-secondary)]">{label}</dt><dd className="col-span-2 whitespace-pre-wrap break-words">{children}</dd></div>);
}

function BidDetail({ id, options, onClose, onEdit }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [party, setParty] = useState(null);
  const [hist, setHist] = useState(null);
  const errorSeq = useRef(0);
  const showError = useCallback((message) => {
    errorSeq.current += 1;
    setErr(message);
  }, []);
  const load = useCallback(async () => {
    const errorAtStart = errorSeq.current;
    try {
      setData(await api.get(`/bids/${id}`));
      // Preserve an error from an action that finished after this reload started.
      if (errorAtStart === errorSeq.current) setErr('');
    } catch (e) { showError(e.message); }
  }, [id, showError]);
  useEffect(() => { load(); }, [load]);
  const pf = useMemo(() => partyFields(options), [options]);
  if (!data) return <Modal title="Bid" onClose={onClose}>{err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm">Loading…</p>}</Modal>;
  const b = data.bid;
  async function saveParty(payload) {
    if (party.id) await api.patch(`/bids/parties/${party.id}`, payload);
    else await api.post(`/bids/${b.id}/parties`, payload);
    setParty(null); load();
  }
  async function removeParty(p) {
    if (!window.confirm(`Remove ${p.name} from this bid?`)) return;
    try { await api.delete(`/bids/parties/${p.id}`); load(); } catch (e) { showError(e.message); }
  }
  async function removeBid() {
    if (!window.confirm('Delete this bid? You can restore it from the Deleted filter.')) return;
    try { await api.delete(`/bids/bids/${b.id}`); onClose(); } catch (e) { showError(e.message); }
  }
  async function restoreBid() {
    try { await api.post(`/bids/bids/${b.id}/restore`); load(); } catch (e) { showError(e.message); }
  }
  return (
    <Modal title={b.title} onClose={onClose} maxWidth="max-w-3xl">
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      {b.deleted_at && <p className="mb-2 text-sm text-rose-600">This bid is deleted. <button className="underline" onClick={restoreBid}>Restore</button></p>}
      <div className="mb-3 flex flex-wrap gap-2">
        <button className={iconBtn} onClick={() => onEdit(b)}><Pencil size={12} /> Edit</button>
        <button className={iconBtn} onClick={() => setHist({ kind: 'bids', row: b, title: b.title })}><History size={12} /> History</button>
        {!b.deleted_at && <button className={`${iconBtn} text-rose-600`} onClick={removeBid}><Trash2 size={12} /> Delete</button>}
        <Link to="/projects" className={iconBtn}>Open Watchlist</Link>
      </div>
      <dl className="divide-y divide-[var(--border)]">
        <Row label="Watchlist project">{b.project?.project_name}</Row>
        <Row label="Owner">{[b.owner_name, b.owner_account_name].filter(Boolean).join(' / ')}</Row>
        <Row label="Location">{b.location}</Row>
        <Row label="Scope">{b.scope}</Row>
        <Row label="Square feet">{b.sqft != null ? b.sqft.toLocaleString() : null}</Row>
        <Row label="Bid due">{b.bid_due_date ? `${fmtDate(b.bid_due_date)}${b.bid_due_note ? `. ${b.bid_due_note}` : ''}` : b.bid_due_note}</Row>
        <Row label="Spec">{[SPEC_LABEL[b.spec_status] || 'Unknown', b.spec_section && `section ${b.spec_section}`, b.spec_note].filter(Boolean).join('. ')}</Row>
        <Row label="Installer partner">{[b.installer_account_name, b.installer_name].filter(Boolean).join(' / ')}</Row>
        <Row label="Material quote">{[b.material_quote_sent === true ? 'Sent' : b.material_quote_sent === false ? 'Not sent' : null, b.quote_ref, b.quote_date && fmtDate(b.quote_date), b.quote_amount_cents != null && usd(b.quote_amount_cents), b.quote_to && `to ${b.quote_to}`].filter(Boolean).join(' · ')}</Row>
        <Row label="Portal proposal">{b.proposal ? `${b.proposal.number || 'Draft'}: ${b.proposal.title || ''} (${b.proposal.status})` : null}</Row>
        <Row label="Concria offer">{b.concria_offer_no}</Row>
        <Row label="CCS lead">{b.ccs_lead}</Row>
        <Row label="Outcome">{b.outcome ? `${OUTCOME_LABEL[b.outcome]}${b.outcome_date ? `, ${fmtDate(b.outcome_date)}` : ''}${b.outcome_note ? `. ${b.outcome_note}` : ''}` : b.outcome_note}</Row>
        <Row label="Long-lead / federal">{b.long_lead || b.federal ? [b.federal && 'Federal', b.long_lead && 'Long lead', b.install_window && `install ${b.install_window}`].filter(Boolean).join(', ') : null}</Row>
        <Row label="Price validity">{b.price_validity}</Row>
        <Row label="Escalation">{b.escalation_assumption}</Row>
        <Row label="Tariffs">{b.tariff_assumption}</Row>
        <Row label="Freight">{[b.freight_assumption, b.freight_valid_until && `valid until ${fmtDate(b.freight_valid_until)}`].filter(Boolean).join('. ')}</Row>
        <Row label="Notes">{b.notes}</Row>
        <Row label="Sources">{(b.sources || []).join(', ')}</Row>
      </dl>

      <div className="mt-4 flex items-center justify-between">
        <h3 className="font-semibold">GCs, subs and partners</h3>
        <button className={iconBtn} onClick={() => setParty({})}><Plus size={12} /> Add</button>
      </div>
      <ul className="mt-2 space-y-1">
        {b.parties.map((p) => (
          <li key={p.id} className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-[var(--border)] p-2 text-sm">
            <div className="min-w-0">
              <div><span className="font-medium">{p.account_name || p.name}</span> <Chip>{ROLE_LABEL[p.role]}</Chip> {p.status && <Chip tone="blue">{PARTY_STATUS[p.status]}</Chip>}</div>
              {(p.contact_name || p.contact_email) && <div className="text-xs text-[var(--text-secondary)]">{[p.contact_name, p.contact_email].filter(Boolean).join(' · ')}</div>}
              {p.note && <div className="text-xs italic text-[var(--text-secondary)]">{p.note}</div>}
            </div>
            <div className="flex gap-1">
              <button className={iconBtn} onClick={() => setParty(p)} aria-label="Edit"><Pencil size={12} /></button>
              <button className={iconBtn} onClick={() => setHist({ kind: 'parties', row: p, title: p.name })} aria-label="History"><History size={12} /></button>
              <button className={`${iconBtn} text-rose-600`} onClick={() => removeParty(p)} aria-label="Remove"><Trash2 size={12} /></button>
            </div>
          </li>
        ))}
        {!b.parties.length && <li className="text-sm text-[var(--text-secondary)]">No one recorded yet.</li>}
      </ul>

      {!!data.specs.length && (<>
        <h3 className="mt-4 font-semibold">Spec records</h3>
        <ul className="mt-1 space-y-1 text-sm">{data.specs.map((s) => <li key={s.id}>{s.firm_name}: <Chip tone={SPEC_REC_TONE[s.status]}>{SPEC_REC_LABEL[s.status]}</Chip></li>)}</ul>
      </>)}
      {!!data.packages.length && (<>
        <h3 className="mt-4 font-semibold">Submittal packages</h3>
        <ul className="mt-1 space-y-1 text-sm">{data.packages.map((p) => (
          <li key={p.id} className="flex items-center gap-2">{p.title}
            <button className={iconBtn} onClick={() => downloadFile(`/bids/packages/${p.id}/pdf`, { method: 'POST', fallbackName: `${p.title}.pdf` }).catch((e) => showError(e.message))}><Download size={12} /> PDF</button>
          </li>))}
        </ul>
      </>)}

      {party && <FormModal title={party.id ? 'Edit' : 'Add GC, sub or partner'} fields={pf} record={party.id ? party : null} onSave={saveParty} onClose={() => setParty(null)} />}
      {hist && <HistoryModal {...hist} onClose={() => setHist(null)} />}
    </Modal>
  );
}

// ── Spec tracking ─────────────────────────────────────────────────────────────
function SpecsTab({ options }) {
  const { rows, err, load, setErr } = useList('/bids/specs', 'specs');
  const [bids, setBids] = useState([]);
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  // Explain when bid choices are unavailable instead of showing only None.
  const [bidsNote, setBidsNote] = useState('');
  useEffect(() => { api.get('/bids').then((r) => setBids(r.bids)).catch(() => setBidsNote('Could not load the bids. Reload the page before linking a spec record to a bid.')); }, []);
  const fields = useMemo(() => [
    { key: 'firm_name', label: 'Firm / program', required: true },
    { key: 'kind', label: 'Kind', type: 'select', options: opts(KIND_LABEL), required: true },
    { key: 'account_id', label: 'CRM company', type: 'picker', options: options.accounts.map((a) => ({ value: a.id, label: a.name })) },
    { key: 'status', label: 'Status', type: 'select', options: opts(SPEC_REC_LABEL), required: true },
    { key: 'program', label: 'Project / owner program', span: 2 },
    { key: 'project_id', label: 'Watchlist project', type: 'picker', options: options.projects.map((p) => ({ value: p.id, label: p.project_name })) },
    { key: 'bid_id', label: 'Bid', type: 'select', options: bids.map((b) => ({ value: b.id, label: b.title })), empty: 'None' },
    { key: 'product', label: 'Product' },
    { key: 'spec_section', label: 'Spec section' },
    { key: 'sample_drop_date', label: 'Sample dropped', type: 'date' },
    { key: 'approval_date', label: 'Approved on', type: 'date' },
    { key: 'approved_by', label: 'Approved by' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ], [options, bids]);
  async function save(payload) {
    if (editing.id) await api.patch(`/bids/specs/${editing.id}`, payload);
    else await api.post('/bids/specs', payload);
    setEditing(null); load();
  }
  async function remove(s) {
    if (!window.confirm(`Delete the spec record for ${s.firm_name}?`)) return;
    try { await api.delete(`/bids/specs/${s.id}`); load(); } catch (e) { setErr(e.message); }
  }
  if (!rows) return err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm text-[var(--text-secondary)]">Loading…</p>;
  const counts = rows.reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {});
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {Object.entries(SPEC_REC_LABEL).filter(([k]) => counts[k]).map(([k, l]) => <Chip key={k} tone={SPEC_REC_TONE[k]}>{l}: {counts[k]}</Chip>)}
        <button onClick={() => setEditing({})} className="ml-auto flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"><Plus size={15} /> Add spec record</button>
      </div>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      {bidsNote && <p className="mb-2 text-sm text-amber-700">{bidsNote}</p>}
      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} className="rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium">{s.firm_name} <Chip>{KIND_LABEL[s.kind]}</Chip> <Chip tone={SPEC_REC_TONE[s.status]}>{SPEC_REC_LABEL[s.status]}</Chip></div>
                <div className="text-xs text-[var(--text-secondary)]">{[s.program, s.account_name && `CRM: ${s.account_name}`, s.project_name && `Watchlist: ${s.project_name}`, s.bid_title && `Bid: ${s.bid_title}`].filter(Boolean).join(' · ')}</div>
                <div className="text-xs text-[var(--text-secondary)]">{[s.product, s.spec_section && `section ${s.spec_section}`, s.sample_drop_date && `sample ${fmtDate(s.sample_drop_date)}`, s.approval_date && `approved ${fmtDate(s.approval_date)}${s.approved_by ? ` by ${s.approved_by}` : ''}`].filter(Boolean).join(' · ')}</div>
                {s.notes && <div className="mt-1 text-xs italic text-[var(--text-secondary)]">{s.notes}</div>}
              </div>
              <div className="flex gap-1">
                <button className={iconBtn} onClick={() => setEditing(s)} aria-label="Edit"><Pencil size={12} /></button>
                <button className={iconBtn} onClick={() => setHist({ kind: 'specs', row: s, title: s.firm_name })} aria-label="History"><History size={12} /></button>
                <button className={`${iconBtn} text-rose-600`} onClick={() => remove(s)} aria-label="Delete"><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-[var(--text-secondary)]">No spec records yet.</p>}
      </div>
      {editing && <FormModal title={editing.id ? 'Edit spec record' : 'New spec record'} fields={fields} record={editing.id ? editing : null} onSave={save} onClose={() => setEditing(null)} />}
      {hist && <HistoryModal {...hist} onClose={() => setHist(null)} />}
    </div>
  );
}

// ── Document library ──────────────────────────────────────────────────────────
const docFields = [
  { key: 'title', label: 'Title', required: true, span: 2 },
  { key: 'doc_type', label: 'Type', type: 'select', options: opts(DOC_LABEL), required: true },
  { key: 'product', label: 'Product' },
  { key: 'version', label: 'Version' },
  { key: 'doc_date', label: 'Document date', type: 'date' },
  { key: 'cleared_public', label: 'Public use', type: 'bool', checkLabel: 'Cleared by Concria for public use' },
  { key: 'source_location', label: 'Where the original lives' },
  { key: 'source_ref', label: 'Evidence / reference' },
  { key: 'notes', label: 'Notes', type: 'textarea', rows: 2 },
];

function DocsTab({ options }) {
  const [showDeleted, setShowDeleted] = useState(false);
  const { rows, err, load, setErr } = useList(showDeleted ? '/bids/assets?deleted=1' : '/bids/assets', 'assets');
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  const [busy, setBusy] = useState(null);
  async function save(payload) {
    if (editing.id) await api.patch(`/bids/assets/${editing.id}`, payload);
    else await api.post('/bids/assets', payload);
    setEditing(null); load();
  }
  async function upload(a, file) {
    if (!file) return;
    setBusy(a.id);
    try { await uploadFile(`/bids/assets/${a.id}/file`, file); load(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }
  async function remove(a) {
    if (!window.confirm(`Delete "${a.title}" from the library?`)) return;
    try { await api.delete(`/bids/assets/${a.id}`); load(); } catch (e) { setErr(e.message); }
  }
  async function restore(a) {
    try { await api.post(`/bids/assets/${a.id}/restore`); load(); } catch (e) { setErr(e.message); }
  }
  if (!rows) return err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm text-[var(--text-secondary)]">Loading…</p>;
  const live = rows.filter((a) => !a.deleted_at);
  const gaps = options.slots.map((s) => {
    const m = {
      data_sheet: (a) => ['pds', 'tds'].includes(a.doc_type), sds_dry_shake: (a) => a.doc_type === 'sds' && !/trowel/i.test(`${a.product} ${a.title}`),
      sds_trowel_hard: (a) => a.doc_type === 'sds' && /trowel/i.test(`${a.product} ${a.title}`), coa: (a) => a.doc_type === 'coa', sieve: (a) => a.doc_type === 'sieve_chart',
      spec: (a) => a.doc_type === 'spec_section', install: (a) => a.doc_type === 'install_guide',
    }[s.key];
    const have = live.filter(m);
    return { ...s, listed: have.length, withFile: have.filter((a) => a.has_file).length };
  });
  return (
    <div>
      <div className="mb-3 rounded-xl border border-[var(--border)] bg-white p-3 text-sm dark:bg-slate-900">
        <div className="mb-1 font-medium">Standard submittal documents</div>
        <div className="flex flex-wrap gap-1">
          {gaps.map((g) => <Chip key={g.key} tone={g.withFile ? 'green' : g.listed ? 'amber' : 'red'}>{g.label}: {g.withFile ? 'file ready' : g.listed ? 'listed, no file' : 'missing'}</Chip>)}
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-[var(--text-secondary)]"><input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show deleted</label>
        <button onClick={() => setEditing({})} className="ml-auto flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"><Plus size={15} /> Add document</button>
      </div>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      <div className="space-y-2">
        {rows.map((a) => (
          <div key={a.id} className={`rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900 ${a.deleted_at ? 'opacity-60' : ''}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium">{a.title}</div>
                <div className="mt-0.5 flex flex-wrap gap-1">
                  <Chip>{DOC_LABEL[a.doc_type]}</Chip>
                  {a.has_file ? <Chip tone="green">File: {a.file_name}</Chip> : <Chip tone="amber">No file uploaded</Chip>}
                  {a.cleared_public ? <Chip tone="green">Cleared for public use</Chip> : <Chip>Internal only</Chip>}
                  {a.deleted_at && <Chip tone="red">Deleted</Chip>}
                </div>
                <div className="mt-1 text-xs text-[var(--text-secondary)]">{[a.product, a.version && `v. ${a.version}`, a.doc_date && fmtDate(a.doc_date)].filter(Boolean).join(' · ')}</div>
                {(a.source_location || a.source_ref) && <div className="text-xs text-[var(--text-secondary)]">{[a.source_location, a.source_ref].filter(Boolean).join(' · ')}</div>}
                {a.notes && <div className="text-xs italic text-[var(--text-secondary)]">{a.notes}</div>}
              </div>
              <div className="flex flex-wrap gap-1">
                {a.deleted_at ? <button className={iconBtn} onClick={() => restore(a)}><RotateCcw size={12} /> Restore</button> : (<>
                  <label className={`${iconBtn} cursor-pointer`}><Upload size={12} /> {busy === a.id ? 'Uploading…' : a.has_file ? 'Replace file' : 'Upload file'}
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => { const file = e.target.files[0]; e.target.value = ''; upload(a, file); }} />
                  </label>
                  {a.has_file && <button className={iconBtn} onClick={() => downloadFile(`/bids/assets/${a.id}/file`, { fallbackName: a.file_name }).catch((e) => setErr(e.message))}><Download size={12} /> Download</button>}
                  <button className={iconBtn} onClick={() => setEditing(a)} aria-label="Edit"><Pencil size={12} /></button>
                  <button className={iconBtn} onClick={() => setHist({ kind: 'assets', row: a, title: a.title })} aria-label="History"><History size={12} /></button>
                  <button className={`${iconBtn} text-rose-600`} onClick={() => remove(a)} aria-label="Delete"><Trash2 size={12} /></button>
                </>)}
              </div>
            </div>
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-[var(--text-secondary)]">No documents yet.</p>}
      </div>
      {editing && <FormModal title={editing.id ? 'Edit document' : 'New document'} fields={docFields} record={editing.id ? editing : null} onSave={save} onClose={() => setEditing(null)} />}
      {hist && <HistoryModal {...hist} onClose={() => setHist(null)} />}
    </div>
  );
}

// ── Submittal packages ────────────────────────────────────────────────────────
function PackagesTab() {
  const { rows, err, load, setErr } = useList('/bids/packages', 'packages');
  const [editing, setEditing] = useState(null);
  const [hist, setHist] = useState(null);
  async function remove(p) {
    if (!window.confirm(`Delete package "${p.title}"?`)) return;
    try { await api.delete(`/bids/packages/${p.id}`); load(); } catch (e) { setErr(e.message); }
  }
  if (!rows) return err ? <p className="text-sm text-rose-600">{err}</p> : <p className="text-sm text-[var(--text-secondary)]">Loading…</p>;
  return (
    <div>
      <div className="mb-3 flex items-center">
        <p className="text-sm text-[var(--text-secondary)]">Pick documents from the library; the PDF gets a cover sheet listing what is inside and what is still missing. Downloads only; nothing is sent.</p>
        <button onClick={() => setEditing({})} className="ml-auto flex shrink-0 items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white"><Plus size={15} /> New package</button>
      </div>
      {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
            <div>
              <div className="font-medium">{p.title}</div>
              <div className="text-xs text-[var(--text-secondary)]">{p.asset_ids.length} {countWord(p.asset_ids.length, 'document')}{p.cover?.prepared_for ? ` · for ${p.cover.prepared_for}` : ''}{p.last_built_at ? ` · last built ${new Date(p.last_built_at).toLocaleString()}` : ''}</div>
            </div>
            <div className="flex flex-wrap gap-1">
              <button className={iconBtn} onClick={() => downloadFile(`/bids/packages/${p.id}/pdf`, { method: 'POST', fallbackName: `${p.title}.pdf` }).then(load).catch((e) => setErr(e.message))}><FileText size={12} /> Build PDF</button>
              <button className={iconBtn} onClick={() => setEditing(p)}><Pencil size={12} /> Edit</button>
              <button className={iconBtn} onClick={() => setHist({ kind: 'packages', row: p, title: p.title })} aria-label="History"><History size={12} /></button>
              <button className={`${iconBtn} text-rose-600`} onClick={() => remove(p)} aria-label="Delete"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {!rows.length && <p className="py-6 text-center text-sm text-[var(--text-secondary)]">No packages yet.</p>}
      </div>
      {editing && <PackageForm pkg={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {hist && <HistoryModal {...hist} onClose={() => setHist(null)} />}
    </div>
  );
}

function PackageForm({ pkg, onClose, onSaved }) {
  const [assets, setAssets] = useState(null);
  const [bids, setBids] = useState([]);
  const [title, setTitle] = useState(pkg.title || '');
  const [bidId, setBidId] = useState(pkg.bid_id || '');
  const [cover, setCover] = useState({ project: '', prepared_for: '', spec_section: '', submitted_by: '', date: '', notes: '', ...(pkg.cover || {}) });
  const [ids, setIds] = useState(pkg.asset_ids || []);
  const [check, setCheck] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // Keep each failed supporting load visible without repeating its warning.
  const [loadNotes, setLoadNotes] = useState([]);
  const noteLoad = (text) => setLoadNotes((n) => (n.includes(text) ? n : [...n, text]));
  // Keep a newly created package ID so a failed PDF build can be retried safely.
  const [savedId, setSavedId] = useState(pkg.id || null);
  // An unknown ID means the save may have succeeded, so prevent another post.
  // Closing reloads the package list so the saved row can be opened there.
  const [idUnknown, setIdUnknown] = useState(false);
  const close = idUnknown ? onSaved : onClose;
  useEffect(() => {
    api.get('/bids/assets').then((r) => setAssets(r.assets)).catch((e) => setErr(e.message));
    api.get('/bids').then((r) => setBids(r.bids)).catch(() => noteLoad('Could not load the bids. Close and reopen this package before changing its bid.'));
    if (pkg.id) api.get(`/bids/packages/${pkg.id}/checklist`).then((r) => setCheck(r.checklist)).catch(() => noteLoad('Could not load the checklist. Close and reopen this package to see it.'));
  }, [pkg.id]);
  const byId = useMemo(() => new Map((assets || []).map((a) => [a.id, a])), [assets]);
  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const move = (i, d) => setIds((cur) => { const n = [...cur]; const j = i + d; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });
  function pickBid(v) {
    setBidId(v);
    const b = bids.find((x) => x.id === v);
    if (b) setCover((c) => ({ ...c, project: c.project || b.title, spec_section: c.spec_section || b.spec_section || '' }));
  }
  async function save(andBuild) {
    if (idUnknown) return;
    if (!title.trim()) { setErr('Title is required'); return; }
    setBusy(true);
    const body = { title: title.trim(), bid_id: bidId || null, cover, asset_ids: ids };
    try {
      await savePackage({ api, downloadFile, id: savedId, body, build: andBuild, fileName: `${title}.pdf`, onSavedId: setSavedId });
      onSaved();
    } catch (e) { setErr(e.message); if (packageIdMissing(e)) setIdUnknown(true); setBusy(false); }
  }
  const coverField = (k, label) => (
    <label key={k} className="block text-sm"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">{label}</span>
      <input className={inputCls} value={cover[k] || ''} onChange={(e) => setCover({ ...cover, [k]: e.target.value })} /></label>
  );
  return (
    <Modal title={pkg.id ? 'Edit submittal package' : 'New submittal package'} onClose={close} maxWidth="max-w-3xl">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Title *</span>
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. P-683 dry shake submittal" /></label>
        <label className="block text-sm sm:col-span-2"><span className="mb-1 block text-xs font-medium text-[var(--text-secondary)]">Bid</span>
          <select className={inputCls} value={bidId} onChange={(e) => pickBid(e.target.value)}><option value="">None</option>{bids.map((b) => <option key={b.id} value={b.id}>{b.title}</option>)}</select></label>
        {coverField('project', 'Cover: project')}
        {coverField('prepared_for', 'Cover: prepared for')}
        {coverField('spec_section', 'Cover: spec section')}
        {coverField('submitted_by', 'Cover: submitted by')}
        {coverField('date', 'Cover: date')}
        {coverField('notes', 'Cover: notes')}
      </div>

      {loadNotes.map((n) => <p key={n} className="mt-3 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">{n}</p>)}
      {check && (
        <div className="mt-4 rounded-lg border border-[var(--border)] p-2 text-xs">
          <div className="mb-1 font-medium">Checklist (as last saved)</div>
          <div className="flex flex-wrap gap-1">{check.map((c) => <Chip key={c.key} tone={c.state === 'included' ? 'green' : c.state === 'missing' ? 'red' : 'amber'}>{c.label}: {({ included: 'included', no_file: 'no file yet', not_selected: 'not selected', missing: 'not in library' })[c.state]}</Chip>)}</div>
        </div>
      )}

      <h3 className="mt-4 text-sm font-semibold">Documents in this package (in order)</h3>
      <ol className="mt-1 space-y-1 text-sm">
        {ids.map((id, i) => { const a = byId.get(id); return (
          <li key={id} className="flex items-center gap-2">
            <span className="w-5 text-right text-xs text-[var(--text-secondary)]">{i + 1}.</span>
            <span className="min-w-0 flex-1 truncate">{a ? a.title : 'Deleted document'}</span>
            {a && !a.has_file && <Chip tone="amber">no file</Chip>}
            {a && a.has_file && !/pdf|image/.test(a.file_mime || '') && <Chip tone="amber">Word: not merged</Chip>}
            <button className={iconBtn} onClick={() => move(i, -1)} aria-label="Up"><ArrowUp size={12} /></button>
            <button className={iconBtn} onClick={() => move(i, 1)} aria-label="Down"><ArrowDown size={12} /></button>
            <button className={iconBtn} onClick={() => toggle(id)} aria-label="Remove"><Trash2 size={12} /></button>
          </li>); })}
        {!ids.length && <li className="text-[var(--text-secondary)]">Pick documents below.</li>}
      </ol>
      <h3 className="mt-4 text-sm font-semibold">Library</h3>
      <div className="mt-1 max-h-60 space-y-1 overflow-y-auto rounded-lg border border-[var(--border)] p-2 text-sm">
        {(assets || []).map((a) => (
          <label key={a.id} className="flex items-center gap-2">
            <input type="checkbox" checked={ids.includes(a.id)} onChange={() => toggle(a.id)} />
            <span className="min-w-0 flex-1 truncate">{a.title}</span>
            <span className="text-xs text-[var(--text-secondary)]">{DOC_LABEL[a.doc_type]}</span>
            {!a.has_file && <Chip tone="amber">no file</Chip>}
          </label>
        ))}
        {!assets && <p className="text-[var(--text-secondary)]">Loading…</p>}
      </div>
      {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={close} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm">{idUnknown ? 'Close' : 'Cancel'}</button>
        <button disabled={busy || idUnknown} onClick={() => save(false)} className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm disabled:opacity-50">Save</button>
        <button disabled={busy || idUnknown} onClick={() => save(true)} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">Save and build PDF</button>
      </div>
    </Modal>
  );
}
